import { useProperty } from 'api/property'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback } from 'react'
import { DialerTabAtom } from '..'
import { Call, Device } from '@twilio/voice-sdk'
import {
  ActiveProperty as ActivePropertyAtom,
  ActiveTicket as ActiveTicketAtom,
  CallStatus,
  DialerAtom,
  DialerStateDefaultValue,
} from '../contact-center.store'
import { contactsFuzzySearch } from './dialer.utils'
import { validatePhoneNumber } from '../contact-center.utils'
import { useCallDurationLabel, useTwilioCallConnection, useTwilioDevice } from '../contact-center.hooks'
import { useQueryClient } from 'react-query'
import { useToast } from '@chakra-ui/react'
import axios from 'axios'

export const useCurrentPropertyId = () => {
  const url = new URL(window.location.href)
  const res = url.pathname.match(new RegExp(`property-details/([^/]+)`))

  if (res && res.length >= 2) {
    return res[1]
  }

  return undefined
}

export const useCurrentTicketId = () => {
  const url = new URL(window.location.href)
  const res = url.pathname.match(new RegExp(`ticket/([^/]+)`))

  if (res && res.length >= 2) {
    return res[1]
  }

  return undefined
}

export const useActivePropertyId = () => {
  const propertyId = useAtomValue(ActivePropertyAtom)
  return propertyId
}

export const useActiveTicketId = () => {
  const ticketId = useAtomValue(ActiveTicketAtom)
  return ticketId
}

export const useDialedNumber = () => {
  const [dialerState, setDialerState] = useAtom(DialerAtom)
  const set = (n: string) => setDialerState(prev => ({ ...prev, number: n }))
  return {
    number: dialerState.number,
    setNumber: set,
  }
}

export const useKeyPadKeyPressed = () => {
  const { dialerState, setDialerState } = useDialerState()
  const set = (n: string) => setDialerState(prev => ({ ...prev, keyPressed: n }))
  return {
    keyPressed: dialerState.keyPressed,
    setKeyPressed: set,
  }
}

export const useDialerState = () => {
  const [dialerState, setDialerState] = useAtom(DialerAtom)
  return {
    dialerState,
    setDialerState,
  }
}

export const usePropertyResidents = (propertyId?: string) => {
  const { propertyData } = useProperty(propertyId)
  return (propertyData?.residents as any[]) || []
}

export const useDialerContacts = (q?: string) => {
  const propertyId = useCurrentPropertyId()
  const contacts = usePropertyResidents(propertyId)

  if (!q) return contacts

  return contactsFuzzySearch(q, contacts) || []
}

export const useActiveContact = () => {
  const { number } = useDialedNumber()
  const contacts = useDialerContacts()
  return (
    contacts.find(c => c.phoneNumber.replace('/D/g', '') === number.replace('/D/g', '')) || {
      phoneNumber: number,
      firstName: '---',
      lastName: '',
    }
  )
}

export const useDialerCallStatus = () => {
  const { dialerState, setDialerState } = useDialerState()

  let state = dialerState.callStatus

  const setter = useCallback(
    (value: CallStatus | ((prevValue: CallStatus) => CallStatus)) => {
      if (typeof value === 'function') {
        state = value(state)
      } else {
        state = value
      }
      setDialerState(prev => ({ ...prev, callStatus: state }))
    },
    [state],
  )

  return {
    status: dialerState.callStatus,
    set: setter,
  }
}

export const useDialerTab = () => {
  const [tab, setTab] = useAtom(DialerTabAtom)
  return {
    tab,
    setTab,
  }
}

export const useTwilioCall = (onDisconnect?: () => void) => {
  const { dialerState, setDialerState } = useDialerState()
  const { set: setCallStatus } = useDialerCallStatus()
  const { setLabel: setDurationLabel } = useCallDurationLabel()
  const { device: twilioDevice }: any = useTwilioDevice()
  const { connection: callConnection, setConnection: setCallConnection } = useTwilioCallConnection()
  const queryClient = useQueryClient()
  const toast = useToast()

  const connectCall = useCallback(
    async (phoneNumber: string) => {
      let device = twilioDevice

      if (!device) {
        try {
          const url = `${process.env.REACT_APP_TWILIO_MICROSERVICE_URL}access-token`

          const identity = process.env.REACT_APP_TWILIO_IDENTITY_CLIENT

          const response = await axios.post<{ token: string }>(url, {
            identity: identity,
          })

          const token = response.data.token

          device = new Device(token, {
            appName: process.env.REACT_APP_TWILIO_CLIENT_NAME,
            closeProtection: true,
            allowIncomingWhileBusy: !!process.env.REACT_APP_TWILIO_ALLOW_INCOMING_WHILE_BUSY,
            maxAverageBitrate: 40000,
          })
        } catch (err) {
          toast({
            title: 'Device not initiated. Please refresh current page and try again or contact system administrator',
            position: 'top-left',
            status: 'error',
            isClosable: true,
          })
          console.error('Twilio Device not initiated')
          return
        }
      }
      if (!validatePhoneNumber(phoneNumber)) {
        toast({
          title: 'Invalid Phone Number.',
          position: 'top-left',
          status: 'error',
          isClosable: true,
        })
        console.error('Invalid Phone Number Supplied', phoneNumber)
        return
      }

      queryClient.invalidateQueries(['call-logs'])
      device.disconnectAll()
      try {
        const connection = await device.connect({
          params: {
            From: process.env.REACT_APP_TWILIO_CALLING_FROM_NUMBER as string,
            To: phoneNumber,
          },
        })

        setDialerState(prev => ({ ...prev, isCalling: true, number: phoneNumber }))

        setCallConnection(connection)

        connection.on('ringing', () => {
          setCallStatus(prev => ({ ...prev, currentLabel: 'Ringing' }))
        })

        connection.on('accept', () => {
          setCallStatus(prev => ({ ...prev, currentLabel: 'In-progress' }))
        })

        connection.on('cancel', () => {
          setCallStatus(prev => ({ ...prev, currentLabel: 'Cancelled' }))
        })

        const startTime = Math.floor(Date.now() / 1000)

        const timeInterval = setInterval(() => {
          const elapsedTime = Math.floor(Date.now() / 1000) - startTime
          const minutes = Math.floor(elapsedTime / 60)
          const seconds = elapsedTime % 60
          const formattedTime = minutes + ':' + (seconds < 10 ? '0' : '') + seconds

          if (connection.status() === Call.State.Open) {
            setDurationLabel(formattedTime)
          }
          if (connection.status() === Call.State.Closed) {
            connection.disconnect()
          }
        }, 1000)

        connection.on('disconnect', conn => {
          console.log('Disconnecting call with status:', conn.status())
          clearInterval(timeInterval)
          onDisconnect && onDisconnect()
        })
      } catch (err) {
        console.log(err)
      }
    },
    [dialerState, setCallStatus, setDialerState, setDurationLabel],
  )

  const muteCall = useCallback(() => {
    if (callConnection?.isMuted()) {
      setCallStatus(prev => ({ ...prev, mute: false }))

      callConnection?.mute(false)
    } else {
      setCallStatus(prev => ({ ...prev, mute: true }))

      callConnection?.mute(true)
    }
  }, [callConnection])

  const endCurrentCall = useCallback(() => {
    callConnection?.disconnect()

    setDialerState(prev => ({ ...prev, isCalling: false }))
    setDurationLabel('')
    setDialerState(DialerStateDefaultValue)

    //onDisconnect && onDisconnect()
  }, [callConnection])

  return {
    connectCall,
    muteCall,
    endCurrentCall,
  }
}
